import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router";
import PageMeta from "../../components/common/PageMeta";
import AuthLayout from "./AuthPageLayout";
import { authService } from "../../services/authService";

// Constante para o nome da chave no localStorage
const PENDING_VERIFICATION_EMAIL_KEY = "pending_verification_email";
// Constante para o tempo de espera entre reenvios (3 minutos em segundos)
const RESEND_COOLDOWN_TIME = 180;
// Constante para o nome da chave no localStorage para o tempo do último reenvio
const LAST_RESEND_TIMESTAMP_KEY = "last_resend_timestamp";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const [verificationStatus, setVerificationStatus] = useState<{
    loading: boolean;
    success?: boolean;
    message?: string;
  }>({ loading: true });
  const [email, setEmail] = useState<string>("");
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const [resendSuccess, setResendSuccess] = useState<boolean>(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: "success" | "error";
  }>({ show: false, message: "", type: "success" });
  const [cooldownTime, setCooldownTime] = useState<number>(0);
  const timerRef = useRef<number | null>(null);

  const showNotification = (message: string, type: "success" | "error") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "success" });
    }, 5000);
  };

  // Função para verificar o tempo de espera
  const checkCooldownTime = () => {
    const lastResendTime = localStorage.getItem(LAST_RESEND_TIMESTAMP_KEY);
    if (lastResendTime) {
      const elapsedSeconds = Math.floor((Date.now() - parseInt(lastResendTime)) / 1000);
      const remainingSeconds = RESEND_COOLDOWN_TIME - elapsedSeconds;

      if (remainingSeconds > 0) {
        setCooldownTime(remainingSeconds);
        startCooldownTimer();
        return true;
      }
    }
    return false;
  };

  // Função para iniciar o timer de contagem regressiva
  const startCooldownTimer = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
    }

    timerRef.current = window.setInterval(() => {
      setCooldownTime(prevTime => {
        if (prevTime <= 1) {
          if (timerRef.current) {
            window.clearInterval(timerRef.current);
            timerRef.current = null;
          }
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  };

  // Formatar o tempo de espera como MM:SS
  const formatCooldownTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    // Limpar o intervalo quando o componente for desmontado
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Verifica se o usuário já está autenticado
    if (authService.isAuthenticated()) {
      // Redireciona para a dashboard
      navigate("/");
      return;
    }

    // Verifica se há um cooldown ativo
    checkCooldownTime();

    // Obtém o token de verificação da URL
    const params = new URLSearchParams(location.search);
    const token = params.get("token");

    // Se tiver um token na URL, verifica ele
    if (token) {
      verifyToken(token);
    } else {
      // Se não tem token, apenas mostra a mensagem para verificar o e-mail
      setVerificationStatus({
        loading: false,
        success: undefined,
        message: "Por favor, verifique seu e-mail para ativar sua conta."
      });

      // Tenta obter o e-mail de diversas fontes
      // 1. Primeiro, do estado de navegação
      const state = location.state as { email?: string } | undefined;
      let emailToUse = state?.email || "";

      // 2. Se não tiver no state, tenta do localStorage
      if (!emailToUse) {
        const storedEmail = localStorage.getItem(PENDING_VERIFICATION_EMAIL_KEY);
        if (storedEmail) {
          emailToUse = storedEmail;
        }
      }

      // Atualiza o state com o e-mail encontrado
      if (emailToUse) {
        setEmail(emailToUse);
      }
    }
  }, [location, navigate]);

  const verifyToken = async (token: string) => {
    try {
      setVerificationStatus({ loading: true });
      const response = await authService.verifyEmail(token);

      if (response.success) {
        // Remove o e-mail do localStorage quando a verificação for bem-sucedida
        localStorage.removeItem(PENDING_VERIFICATION_EMAIL_KEY);
        // Remove o timestamp do último reenvio quando verificação é concluída
        localStorage.removeItem(LAST_RESEND_TIMESTAMP_KEY);

        setVerificationStatus({
          loading: false,
          success: true,
          message: "Seu e-mail foi verificado com sucesso! Aguarde..."
        });
        // Aguarda 3 segundos e redireciona para a página de login
        setTimeout(() => {
          navigate("/onboarding");
        }, 3000);
      } else {
        setVerificationStatus({
          loading: false,
          success: false,
          message: response.message || "O link de verificação é inválido ou expirou."
        });
      }
    } catch (error) {
      setVerificationStatus({
        loading: false,
        success: false,
        message: "Ocorreu um erro ao verificar seu e-mail. Por favor, tente novamente mais tarde."
      });
    }
  };

  const handleResendEmail = async () => {
    // Se estiver em cooldown, não permite reenvio
    if (cooldownTime > 0) {
      return;
    }

    // Verifica se temos o e-mail no state
    let emailToUse = email;

    // Se não tiver no state, tenta buscar do localStorage
    if (!emailToUse) {
      const storedEmail = localStorage.getItem(PENDING_VERIFICATION_EMAIL_KEY);
      if (storedEmail) {
        emailToUse = storedEmail;
        setEmail(storedEmail); // Atualiza o state
      }
    }

    // Se mesmo assim não tiver e-mail, mostra notificação
    if (!emailToUse) {
      showNotification("Não foi possível reenviar o código. E-mail não disponível.", "error");
      return;
    }

    try {
      setResendLoading(true);
      const response = await authService.resendVerificationEmail(emailToUse);

      if (response.success) {
        setResendSuccess(true);
        showNotification("E-mail de verificação reenviado com sucesso!", "success");

        // Inicia o período de cooldown
        const currentTime = Date.now();
        localStorage.setItem(LAST_RESEND_TIMESTAMP_KEY, currentTime.toString());
        setCooldownTime(RESEND_COOLDOWN_TIME);
        startCooldownTimer();
      } else {
        setResendSuccess(false);
        showNotification(response.message || "Falha ao reenviar o e-mail. Tente novamente.", "error");
      }
    } catch (error) {
      setResendSuccess(false);
      showNotification("Ocorreu um erro ao reenviar o código. Tente novamente mais tarde.", "error");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <>
      <PageMeta
        title="Verificação de E-mail - Sellyo"
        description="Verifique seu e-mail para ativar sua conta"
      />
      <AuthLayout>
        {notification.show && (
          <div
            className={`fixed top-4 right-4 z-50 rounded-md p-4 shadow-md transition-all duration-300 ${notification.type === "success"
              ? "bg-success-500 text-white"
              : "bg-error-500 text-white"
              }`}
          >
            <div className="flex items-center">
              {notification.type === "success" ? (
                <svg
                  className="mr-2 h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              ) : (
                <svg
                  className="mr-2 h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              )}
              <span>{notification.message}</span>
            </div>
          </div>
        )}

        <div className="flex items-center justify-center w-full px-4 py-10 lg:w-1/2 sm:p-12.5 xl:p-17.5">
          <div className="w-full max-w-md">
            <div className="flex flex-col items-center">
              <img
                src="/images/error/verify-email.svg"
                alt="Verificação de E-mail"
                className="w-56 h-56 mb-6 dark:invert dark:brightness-90 dark:hue-rotate-180"
              />

              {verificationStatus.loading ? (
                <div className="flex flex-col items-center mt-4">
                  <div className="w-10 h-10 border-4 border-t-4 border-gray-200 rounded-full animate-spin dark:border-gray-700 border-t-primary"></div>
                  <p className="mt-4 text-base text-body-color dark:text-body-color-dark">
                    Verificando seu e-mail...
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  {verificationStatus.success === undefined && (
                    <>
                      <h1 className="mt-6 mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                        Verifique seu e-mail
                      </h1>

                      <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
                        Enviamos um link de verificação para seu e-mail. Acesse o link para verificar sua conta e completar seu cadastro.
                      </p>

                      <div className="flex flex-col items-center mt-8">
                        <div className="flex items-center justify-center mb-4">
                          <p className="text-sm text-gray-500 dark:text-gray-400 mr-3">
                            Não recebeu o e-mail?
                          </p>
                          <button
                            onClick={handleResendEmail}
                            disabled={resendLoading || cooldownTime > 0}
                            className="text-sm text-gray-500 dark:text-gray-400 hover:underline focus:outline-none disabled:opacity-50 disabled:hover:no-underline"
                          >
                            {resendLoading
                              ? "Reenviando..."
                              : cooldownTime > 0
                                ? `Aguarde ${formatCooldownTime(cooldownTime)}`
                                : "Reenviar código"}
                          </button>
                        </div>

                        <Link
                          to="/signin"
                          className="flex items-center text-sm text-gray-500 dark:text-gray-400 hover:underline mt-4"
                        >
                          <svg
                            className="w-4 h-4 mr-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M15 19l-7-7 7-7"
                            ></path>
                          </svg>
                          Voltar para o login
                        </Link>

                        {resendSuccess && (
                          <p className="mt-4 text-sm text-meta-3">
                            E-mail de verificação reenviado com sucesso!
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  {verificationStatus.success === true && (
                    <>
                      <h1 className="mt-6 mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                        E-mail verificado!
                      </h1>

                      <p className="mt-10 mb-6 text-base text-gray-500 dark:text-gray-400 sm:text-lg">
                        {verificationStatus.message}
                      </p>

                      <Link
                        to="/signin"
                        className="inline-flex items-center justify-center px-6 py-3 mt-4 text-base font-medium text-white rounded-md bg-primary hover:bg-opacity-90"
                      >
                        Fazer Login
                      </Link>
                    </>
                  )}

                  {verificationStatus.success === false && (
                    <>
                      <h1 className="mt-6 mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                        A verificação falhou
                      </h1>

                      <p className="mt-10 mb-6 text-base text-gray-500 dark:text-gray-400 sm:text-lg">
                        {verificationStatus.message}
                      </p>

                      <div className="flex flex-col items-center mt-4 space-y-3">
                        <Link
                          to="/signup"
                          className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white rounded-md bg-primary hover:bg-opacity-90"
                        >
                          Criar nova conta
                        </Link>

                        <Link
                          to="/signin"
                          className="flex items-center text-sm text-gray-500 dark:text-gray-400 hover:underline mt-4"
                        >
                          <svg
                            className="w-4 h-4 mr-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M15 19l-7-7 7-7"
                            ></path>
                          </svg>
                          Voltar para o login
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </AuthLayout>
    </>
  );
}